import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import {
  PiggyBank,
  ShieldCheck,
  DeviceMobile as MonitorSmartphone,
} from '@phosphor-icons/react'
import { useAuth } from './lib/auth'
import { useRole } from './lib/useRole'
import { RoleGuard } from './components/RoleGuard'
import { LoginPage } from './routes/LoginPage'
import { NeoShell } from './components/layout/neo-shell'
import { SolicitudesPage } from './routes/SolicitudesPage'
import { BajasPage } from './routes/BajasPage'
import { ImportPage } from './routes/ImportPage'
import { AsociadosPage } from './routes/AsociadosPage'
import { DashboardPage } from './routes/DashboardPage'
import { PlaceholderPage } from './routes/PlaceholderPage'
import { LegalPage } from './routes/LegalPage'
import { NeoCoreAsociadosPage } from './routes/NeoCoreAsociadosPage'
import { NeoCoreAsociadoFichaPage } from './routes/NeoCoreAsociadoFichaPage'
import { NeoCoreInscribirAsociadoPage } from './routes/NeoCoreInscribirAsociadoPage'
import { NeoCoreCredencialesAppPage } from './routes/NeoCoreCredencialesAppPage'
import { NeoCoreMigracionOnboardingPage } from './routes/NeoCoreMigracionOnboardingPage'
import { NeoCoreParametrosPage } from './routes/NeoCoreParametrosPage'
import { NeoCoreBancosPage } from './routes/NeoCoreBancosPage'
import { NeoCoreCajaPage } from './routes/NeoCoreCajaPage'
import { NeoCorePlazosFijosPage } from './routes/NeoCorePlazosFijosPage'
import { NeoCorePlazoFijoDetallePage } from './routes/NeoCorePlazoFijoDetallePage'
import { NeoCoreCreditoDetallePage } from './routes/NeoCoreCreditoDetallePage'
import { NeoCoreCreditosPage } from './routes/NeoCoreCreditosPage'
import { NeoCoreCobranzaPage } from './routes/NeoCoreCobranzaPage'
import { NeoCoreCondonacionesMoraPage } from './routes/NeoCoreCondonacionesMoraPage'
import { NeoCoreTestCobranzaPage } from './routes/NeoCoreTestCobranzaPage'
import { NeoCoreCampanaInscripcionPage } from './routes/NeoCoreCampanaInscripcionPage'
import { NeoCoreConciliacionMensualPage } from './routes/NeoCoreConciliacionMensualPage'
import NeoCoreCreditosOriginacionPage from './routes/NeoCoreCreditosOriginacionPage'
import NeoCoreCotizadorPage from './routes/NeoCoreCotizadorPage'
import NeoCoreInvitarCreditoPage from './routes/NeoCoreInvitarCreditoPage'
import { NeoCorePresaCrediticiaPage } from './routes/NeoCorePresaCrediticiaPage'
import { NeoCoreReferidosPage } from './routes/NeoCoreReferidosPage'
import { NeoCoreCatalogoContablePage } from './routes/NeoCoreCatalogoContablePage'
import { NeoCoreCobranzaCockpitPage } from './routes/NeoCoreCobranzaCockpitPage'
import { NeoCoreBICreditosPage } from './routes/NeoCoreBICreditosPage'
import { NeoCoreBIMomentumPage } from './routes/NeoCoreBIMomentumPage'
import { NeoCorePanelVivoPage } from './routes/NeoCorePanelVivoPage'
import { NeoCoreVerificacionesBancariasPage } from './routes/NeoCoreVerificacionesBancariasPage'
import { NeoCoreVerificacionesAnalistaPage } from './routes/NeoCoreVerificacionesAnalistaPage'
import { NeoCoreOkidTestPage } from './routes/NeoCoreOkidTestPage'
import { NeoCoreOkidVerificacionesPage } from './routes/NeoCoreOkidVerificacionesPage'
import { NeoCoreCasosAmlPage } from './routes/NeoCoreCasosAmlPage'
import { NeoCoreOkidConfigPage } from './routes/NeoCoreOkidConfigPage'
import { NeoCoreBitacoraOfacPage } from './routes/NeoCoreBitacoraOfacPage'
import { NeoCoreRetirosPage } from './routes/NeoCoreRetirosPage'
import { NeoCoreContabilidadPage } from './routes/NeoCoreContabilidadPage'
import { NeoCoreGastosPage } from './routes/NeoCoreGastosPage'
import { NeoCoreProveedoresPage } from './routes/NeoCoreProveedoresPage'
import { NeoCoreReportesPagoPage } from './routes/NeoCoreReportesPagoPage'
import { NeoCoreReportesPage } from './routes/NeoCoreReportesPage'
import { NeoCoreConciliacionPage } from './routes/NeoCoreConciliacionPage'
import { NeoCoreActividadPage } from './routes/NeoCoreActividadPage'
import { NeoCoreErroresPage } from './routes/NeoCoreErroresPage'
import { NeoCoreNotificacionesPage } from './routes/NeoCoreNotificacionesPage'
import { NeoCorePlanillasPage } from './routes/NeoCorePlanillasPage'
import { NeoCorePolizaManualPage } from './routes/NeoCorePolizaManualPage'
import { NeoCoreReportesContablesPage } from './routes/NeoCoreReportesContablesPage'
import { NeoCoreMovimientosCajaPage } from './routes/NeoCoreMovimientosCajaPage'
import { NeoCoreCarterasPage } from './routes/NeoCoreCarterasPage'
import { NeoCoreConfigReportePage } from './routes/NeoCoreConfigReportePage'
import { NeoCoreConciliacionBancariaPage } from './routes/NeoCoreConciliacionBancariaPage'
import { NeoCoreReporteInteresesPage } from './routes/NeoCoreReporteInteresesPage'
import { NeoCoreDatosSatPage } from './routes/NeoCoreDatosSatPage'
import { NeoCoreUsuariosPage } from './routes/NeoCoreUsuariosPage'
import { NeoCoreModulePlaceholder } from './routes/NeoCoreModulePlaceholder'
import { LoadingNeocoop } from './components/LoadingNeocoop'

export default function App() {
  const { session, loading } = useAuth()
  const { role } = useRole()

  // Splash mínimo de 1100ms aunque la sesión hidrate al toque — para que
  // el isotipo brand alcance a respirar en pantalla. Sensación premium.
  const [splashDone, setSplashDone] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setSplashDone(true), 1100)
    return () => clearTimeout(t)
  }, [])

  // Gate de hidratación: mientras Supabase hidrata la sesión desde localStorage,
  // o mientras dura el splash mínimo brand.
  if (loading || !splashDone) {
    return <LoadingNeocoop fullscreen label="Cargando NeoCore…" />
  }

  // Sin sesión: solo login.
  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  // Con sesión pero sin ningún rol staff (admin/contador/backoffice): no
  // dejamos entrar al panel. Mostramos aviso y botón para cerrar sesión.
  if (!role) {
    return <NotAdminGate />
  }

  // Sesión + rol staff: panel. La granularidad por módulo la maneja RoleGuard.
  return (
    <Routes>
      <Route element={<NeoShell />}>
        <Route index element={<DashboardPage />} />

        {/* NeoCore — el core nuevo */}
        <Route path="/neocore/asociados"      element={<NeoCoreAsociadosPage />} />
        <Route path="/neocore/asociar/nuevo"  element={<NeoCoreInscribirAsociadoPage />} />
        <Route path="/neocore/credenciales-app" element={<NeoCoreCredencialesAppPage />} />
        <Route path="/neocore/migracion-onboarding" element={<NeoCoreMigracionOnboardingPage />} />
        <Route path="/neocore/asociados/:id"  element={<NeoCoreAsociadoFichaPage />} />
        <Route path="/neocore/plazos-fijos"        element={<NeoCorePlazosFijosPage />} />
        <Route path="/neocore/plazos-fijos/:id"    element={<NeoCorePlazoFijoDetallePage />} />
        <Route path="/neocore/parametros"     element={<NeoCoreParametrosPage />} />

        <Route
          path="/neocore/ahorros"
          element={
            <NeoCoreModulePlaceholder
              module="Ahorros y aportaciones"
              Icon={PiggyBank}
              description="Captación pasiva del asociado: aportaciones obligatorias mensuales, ahorro corriente sin restricción de retiro y depósitos a plazo fijo con tasa preferencial. Cada producto genera devengo de intereses mensual."
              comingFeatures={[
                'Cuenta de aportaciones obligatorias (1 por asociado)',
                'Ahorro corriente — N cuentas por asociado, sin penalidad de retiro',
                'Plazo fijo — fechas de vencimiento, tasa por monto/plazo, capitalización',
                'Devengo automático mensual de intereses (cierre)',
                'Pignoración para garantía de crédito',
                'Movimientos: depósito, retiro, transferencia, interés, comisión, ajuste',
              ]}
            />
          }
        />

        <Route path="/neocore/creditos"              element={<NeoCoreCreditosPage />} />
        <Route path="/neocore/creditos/bi"           element={<RoleGuard module="creditos"><NeoCoreBICreditosPage /></RoleGuard>} />
        <Route path="/neocore/creditos/bi-momentum"  element={<RoleGuard module="creditos"><NeoCoreBIMomentumPage /></RoleGuard>} />
        <Route path="/neocore/panel-vivo"            element={<RoleGuard module="creditos"><NeoCorePanelVivoPage /></RoleGuard>} />
        <Route path="/neocore/cobranza"              element={<RoleGuard module="cobranza"><NeoCoreCobranzaPage /></RoleGuard>} />
        <Route path="/neocore/test-cobranza"         element={<RoleGuard module="cobranza"><NeoCoreTestCobranzaPage /></RoleGuard>} />
        <Route path="/neocore/campana-inscripcion"   element={<RoleGuard module="inscripciones"><NeoCoreCampanaInscripcionPage /></RoleGuard>} />
        <Route path="/neocore/condonaciones-mora"    element={<RoleGuard module="creditos"><NeoCoreCondonacionesMoraPage /></RoleGuard>} />
        <Route path="/neocore/creditos/originacion"  element={<NeoCoreCreditosOriginacionPage />} />
        <Route path="/neocore/cotizador"             element={<NeoCoreCotizadorPage />} />
        <Route path="/neocore/invitar-credito"       element={<NeoCoreInvitarCreditoPage />} />
        <Route path="/neocore/creditos/:id"          element={<NeoCoreCreditoDetallePage />} />
        <Route
          path="/neocore/presa-crediticia"
          element={
            <RoleGuard module="creditos">
              <NeoCorePresaCrediticiaPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/referidos"
          element={
            <RoleGuard module="creditos">
              <NeoCoreReferidosPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/catalogo-contable"
          element={
            <RoleGuard module="contabilidad">
              <NeoCoreCatalogoContablePage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/cobranza-cockpit"
          element={
            <RoleGuard module="cobranza">
              <NeoCoreCobranzaCockpitPage />
            </RoleGuard>
          }
        />
        <Route path="/neocore/reportes-pago" element={<NeoCoreReportesPagoPage />} />
        <Route path="/neocore/actividad" element={<NeoCoreActividadPage />} />
        <Route path="/neocore/errores" element={<NeoCoreErroresPage />} />
        <Route path="/neocore/notificaciones" element={<NeoCoreNotificacionesPage />} />
        <Route path="/neocore/planillas" element={<NeoCorePlanillasPage />} />
        <Route path="/neocore/reportes" element={<NeoCoreReportesPage />} />
        <Route path="/neocore/conciliacion-onetap" element={<NeoCoreConciliacionPage />} />
        <Route path="/neocore/verificaciones-bancarias" element={<NeoCoreVerificacionesBancariasPage />} />

        <Route
          path="/neocore/retiros"
          element={
            <RoleGuard module="bancos">
              <NeoCoreRetirosPage />
            </RoleGuard>
          }
        />

        <Route path="/neocore/caja" element={<NeoCoreCajaPage />} />

        <Route
          path="/neocore/bancos"
          element={
            <RoleGuard module="bancos">
              <NeoCoreBancosPage />
            </RoleGuard>
          }
        />

        <Route path="/neocore/conciliacion-mensual" element={<RoleGuard module="contabilidad"><NeoCoreConciliacionMensualPage /></RoleGuard>} />
        <Route
          path="/neocore/contabilidad"
          element={
            <RoleGuard module="contabilidad">
              <NeoCoreContabilidadPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/contabilidad/poliza-nueva"
          element={
            <RoleGuard module="contabilidad">
              <NeoCorePolizaManualPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/contabilidad/poliza/:id/editar"
          element={
            <RoleGuard module="contabilidad">
              <NeoCorePolizaManualPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/contabilidad/reportes"
          element={
            <RoleGuard module="contabilidad">
              <NeoCoreReportesContablesPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/movimientos-caja"
          element={
            <RoleGuard module="contabilidad">
              <NeoCoreMovimientosCajaPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/carteras"
          element={
            <RoleGuard module="contabilidad">
              <NeoCoreCarterasPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/config-reporte"
          element={
            <RoleGuard module="contabilidad">
              <NeoCoreConfigReportePage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/conciliacion-bancaria"
          element={
            <RoleGuard module="contabilidad">
              <NeoCoreConciliacionBancariaPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/reporte-intereses"
          element={
            <RoleGuard module="contabilidad">
              <NeoCoreReporteInteresesPage />
            </RoleGuard>
          }
        />
        <Route
          path="/neocore/datos-sat"
          element={
            <RoleGuard module="asociados">
              <NeoCoreDatosSatPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/gastos"
          element={
            <RoleGuard module="gastos">
              <NeoCoreGastosPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/proveedores"
          element={
            <RoleGuard module="proveedores">
              <NeoCoreProveedoresPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/usuarios"
          element={
            <RoleGuard module="usuarios_staff">
              <NeoCoreUsuariosPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/cumplimiento"
          element={
            <NeoCoreModulePlaceholder
              module="Cumplimiento"
              Icon={ShieldCheck}
              description="KYC con motor de visión propio, monitoreo PEP/CPE, cola de revisión humana de KYCs marcados como 'review', generación de reportes IVE/UIF y archivo regulatorio. Incluye la cola de KYC pendiente del módulo Asociados."
              comingFeatures={[
                'Cola de KYCs en estado review (revisión humana)',
                'Verificación de identidad con motor de visión propio — DPI frente/dorso, recibo de servicios',
                'Monitoreo continuo de listas PEP/CPE',
                'Reportes IVE/UIF (formularios IR-01/02/03)',
                'Detección de operaciones sospechosas',
                'Archivo regulatorio (envío a INGECOP)',
              ]}
            />
          }
        />

        <Route
          path="/neocore/cumplimiento/verificaciones-analista"
          element={
            <RoleGuard module="kyc">
              <NeoCoreVerificacionesAnalistaPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/cumplimiento/okid-test"
          element={
            <RoleGuard module="kyc">
              <NeoCoreOkidTestPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/cumplimiento/okid-verificaciones"
          element={
            <RoleGuard module="kyc">
              <NeoCoreOkidVerificacionesPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/cumplimiento/okid-config"
          element={
            <RoleGuard module="kyc">
              <NeoCoreOkidConfigPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/cumplimiento/casos-aml"
          element={
            <RoleGuard module="kyc">
              <NeoCoreCasosAmlPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/cumplimiento/bitacora-ofac"
          element={
            <RoleGuard module="kyc">
              <NeoCoreBitacoraOfacPage />
            </RoleGuard>
          }
        />

        <Route
          path="/neocore/sucursal"
          element={
            <NeoCoreModulePlaceholder
              module="Sucursal digital"
              Icon={MonitorSmartphone}
              description="Configuración de la sucursal digital del asociado en la app móvil: qué operaciones puede hacer (depositar, transferir, solicitar crédito, ver estado de cuenta), límites diarios, autorizaciones requeridas."
              comingFeatures={[
                'Configuración de operaciones permitidas por tipo de asociado',
                'Límites diarios/mensuales por operación',
                'Autorización de doble factor (Face ID + PIN)',
                'Notificaciones push por evento',
                'Estado de cuenta digital (PDF + JSON descargable)',
                'Tickets de soporte desde la app',
              ]}
            />
          }
        />

        {/* Temporal — auxiliares mientras NeoCore no completa todo */}
        <Route path="/solicitudes" element={<SolicitudesPage />} />
        <Route path="/bajas" element={<BajasPage />} />
        <Route path="/asociados" element={<AsociadosPage />} />
        <Route
          path="/cuentas"
          element={
            <PlaceholderPage
              title="Cuentas"
              description="Listado de productos financieros (ahorro, aportaciones, plazo fijo). Próximamente."
            />
          }
        />
        <Route path="/importar" element={<ImportPage />} />

        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

function NotAdminGate() {
  const { signOut, session } = useAuth()
  return (
    <div className="login-root">
      <div className="login-card">
        <div className="login-content" style={{ textAlign: 'center' }}>
          <img src="/brand/logo-horizontal.png" alt="neocoop." className="login-logo" style={{ alignSelf: 'center', marginBottom: 24 }} />

          <h1 className="login-title" style={{ textAlign: 'center' }}>Acceso en preparación</h1>
          <p className="login-sub" style={{ textAlign: 'center' }}>
            Tu cuenta <strong>{session?.user?.email}</strong> está creada, pero
            aún no tiene un rol staff (admin, contador o backoffice). Pedile a
            un admin del equipo que te asigne uno desde <strong>/neocore/usuarios</strong>.
          </p>

          <button className="login-submit" onClick={signOut} style={{ marginTop: 8 }}>
            Cambiar de cuenta
          </button>
        </div>
      </div>
    </div>
  )
}
